<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import BranchRow from '@/components/location/BranchRow.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import type { MapMarker } from '@/composables/useRestaurants'

const props = withDefaults(defineProps<Props>(), { activeId: '', query: '', origin: undefined })

const emit = defineEmits<{
  select: [branch: MapMarker]
  locate: []
}>()

const { t } = useI18n()

/**
 * Прокручиваемая часть шита самовывоза: «Показать ближайшие» и строки филиалов.
 * Сортировка по расстоянию включается вручную — иначе пришлось бы просить
 * геолокацию у всех, кому она не нужна.
 */
interface Props {
  branches: MapMarker[]
  activeId?: string
  /** Запрос из поиска в липкой шапке шита */
  query?: string
  /** Точка клиента для сортировки по близости */
  origin?: [number, number] | undefined
}

const byDistance = ref(false)

/** Грубый квадрат расстояния: для сортировки корень и метры не нужны. */
function distance(branch: MapMarker): number {
  if (!props.origin) return 0
  const [lon, lat] = props.origin
  return (branch.coordinates[0] - lon) ** 2 + (branch.coordinates[1] - lat) ** 2
}

const visible = computed(() => {
  const query = props.query.trim().toLowerCase()
  const found = query
    ? props.branches.filter(
        (branch) =>
          branch.title.toLowerCase().includes(query) ||
          (branch.address ?? '').toLowerCase().includes(query),
      )
    : [...props.branches]

  return byDistance.value && props.origin
    ? found.sort((a, b) => distance(a) - distance(b))
    : found
})

function locate(): void {
  byDistance.value = true
  emit('locate')
}
</script>

<template>
  <ul class="branch-list">
    <li>
      <button type="button" class="branch-list__nearest" @click="locate">
        <!-- Иконка из макета уже с тёмным кругом и жёлтыми кольцами — обёртка не нужна -->
        <AppIcon name="coin-target" :size="24" class="branch-list__nearest-icon" />
        <span class="branch-list__nearest-text font-16-b">{{ t('branch.nearest') }}</span>
        <span class="branch-list__chevron">
          <AppIcon name="arrow-right" :size="14" />
        </span>
      </button>
    </li>

    <li v-for="branch in visible" :key="branch.id">
      <BranchRow :branch="branch" :active="branch.id === activeId" @select="emit('select', branch)" />
    </li>

    <li v-if="!visible.length" class="branch-list__empty font-14-m">{{ t('branch.notFound') }}</li>
  </ul>
</template>

<style scoped lang="scss">
.branch-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-top: var(--screen-padding);
  padding-bottom: var(--tg-safe-area-bottom);

  &__nearest {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    height: 38px;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
  }

  /* Тёмный кружок 24 с жёлтой стрелкой внутри */
  &__nearest-icon {
    flex: 0 0 24px;
  }

  &__nearest-text {
    margin-inline-end: auto;
    line-height: 24px;
    color: var(--primary-500);
  }

  &__chevron {
    display: flex;
    flex: 0 0 24px;
    align-items: center;
    justify-content: center;
    height: 24px;
    border-radius: var(--radius-s);
    background: var(--surface-muted);
    color: var(--icon-color);
  }

  &__empty {
    padding: 20px 0;
    color: var(--text-color-secondary);
    text-align: center;
  }
}
</style>
